import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { createClient } from '@supabase/supabase-js';
import { getCart, removeFromCart, updateQuantity } from '../../utils/cart';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

interface CartItem {
  id: number;
  name: string;
  price: number;
  quantity: number;
  commission: number;
  recurring?: boolean;
  category?: string;
  image: string;
}

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderNumber, setOrderNumber] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'paypal' | 'apple' | 'google'>('card');
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States',
    cardNumber: '',
    expiry: '',
    cvv: '',
    cardName: '',
    paypalEmail: ''
  });

  // Load cart from localStorage
  useEffect(() => {
    const loadCart = () => {
      try {
        const cart = getCart();
        console.log('Loading cart:', cart); // Debug log
        setCartItems(cart);
        setLoading(false);
        
        if (cart.length === 0) {
          console.log('Cart is empty'); // Debug log
        }
      } catch (error) {
        console.error('Error loading cart:', error);
        setLoading(false);
      }
    };

    loadCart();

    // Listen for cart updates
    const handleCartUpdate = (event: CustomEvent) => {
      console.log('Cart updated:', event.detail); // Debug log
      setCartItems(event.detail);
    };

    window.addEventListener('cartUpdated', handleCartUpdate as EventListener);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate as EventListener);
  }, []);

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const totalCommission = cartItems.reduce((sum, item) => sum + item.commission * item.quantity, 0);
  const tax = subtotal * 0.1;
  const total = subtotal + tax;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRemoveItem = (itemId: number) => {
    console.log('Removing item:', itemId); // Debug log
    removeFromCart(itemId);
    const updatedCart = getCart();
    setCartItems(updatedCart);
  };

  const handleUpdateQuantity = (itemId: number, newQuantity: number) => {
    console.log('Updating quantity:', itemId, newQuantity); // Debug log
    if (newQuantity < 1) {
      handleRemoveItem(itemId);
    } else {
      updateQuantity(itemId, newQuantity);
      const updatedCart = getCart();
      setCartItems(updatedCart);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.email || !formData.firstName || !formData.lastName || !formData.address || 
        !formData.city || !formData.state || !formData.zip) {
      alert('Please fill in all shipping information');
      return;
    }

    // Validate payment method specific fields
    if (paymentMethod === 'card') {
      if (!formData.cardNumber || !formData.expiry || !formData.cvv || !formData.cardName) {
        alert('Please fill in all payment details');
        return;
      }
    } else if (paymentMethod === 'paypal') {
      if (!formData.paypalEmail) {
        alert('Please enter your PayPal email');
        return;
      }
    }

    setProcessing(true);

    try {
      // Generate order ID
      const orderId = `ORD-${Date.now()}`;
      const customerName = `${formData.firstName} ${formData.lastName}`;

      // Calculate commission (30% default)
      const commissionRate = 30;

      // Determine category from cart items
      const category = cartItems.length > 0 ? (cartItems[0].category || 'Products') : 'Products';

      // Save order to Supabase
      const { error: insertError } = await supabase
        .from('orders')
        .insert({
          order_id: orderId,
          order_date: new Date().toISOString().split('T')[0],
          customer_name: customerName,
          customer_email: formData.email,
          products: cartItems.map(item => `${item.name} (x${item.quantity})`).join(', '),
          total_amount: total,
          commission_amount: totalCommission,
          commission_rate: commissionRate,
          status: 'Completed',
          category: category
        });

      if (insertError) {
        console.error('Error saving order:', insertError);
        throw insertError;
      }

      // Send notification to admin
      try {
        const notificationResponse = await fetch(
          `${supabaseUrl}/functions/v1/send-order-notification`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              orderId: orderId,
              orderData: {
                customer_name: customerName,
                customer_email: formData.email,
                products: cartItems.map(item => `${item.name} (x${item.quantity})`).join(', '),
                total_amount: total,
                commission_amount: totalCommission,
                commission_rate: commissionRate
              }
            })
          }
        );

        if (!notificationResponse.ok) {
          console.error('Failed to send notification');
        }
      } catch (notificationError) {
        console.error('Notification error:', notificationError);
        // Don't fail the order if notification fails
      }

      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 2000));

      setOrderComplete(true);
      setOrderNumber(orderId);
      
      // Clear cart after successful order
      localStorage.removeItem('cart');
      
      // Redirect to dashboard after 3 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);

    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Show order confirmation
  if (orderComplete) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-32 pb-24 px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-12 shadow-lg">
              <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-6">
                <i className="ri-check-line text-4xl text-green-600"></i>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Order Confirmed!
              </h1>
              <p className="text-gray-600 mb-6">
                Thank you for your purchase. Your order has been successfully placed.
              </p>
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <p className="text-sm text-gray-600 mb-2">Order Number</p>
                <p className="text-2xl font-bold text-primary">{orderNumber}</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-800">
                  <strong>You earned ${totalCommission.toFixed(2)} commission!</strong>
                </p>
              </div>
              <p className="text-sm text-gray-500 mb-6">
                Redirecting to dashboard...
              </p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show empty cart message
  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-32 pb-24 px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-12 shadow-lg">
              <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-6">
                <i className="ri-shopping-cart-line text-4xl text-gray-400"></i>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Your Cart is Empty
              </h1>
              <p className="text-gray-600 mb-6">
                Add some products to your cart before checking out.
              </p>
              <button
                onClick={() => navigate('/products')}
                className="px-8 py-3 bg-gradient-to-r from-primary to-accent-green text-white rounded-lg font-semibold hover:shadow-xl transition-all cursor-pointer whitespace-nowrap"
              >
                Browse Products
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-32 pb-24 px-6">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-12 shadow-lg">
              <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-6 animate-pulse">
                <i className="ri-loader-4-line text-4xl text-gray-400 animate-spin"></i>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                Loading your cart...
              </h1>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="pt-32 pb-24 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Progress Indicator */}
          <div className="mb-12">
            <div className="flex items-center justify-center gap-4">
              {[
                { num: 1, label: 'Cart' },
                { num: 2, label: 'Information' },
                { num: 3, label: 'Payment' },
                { num: 4, label: 'Confirmation' },
              ].map((s, index) => (
                <div key={s.num} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all ${
                        step >= s.num
                          ? 'bg-gradient-to-r from-primary to-accent-green text-white'
                          : step === s.num
                          ? 'bg-white border-2 border-primary text-primary ring-4 ring-primary/20'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {step > s.num ? <i className="ri-check-line"></i> : s.num}
                    </div>
                    <span className="text-xs mt-2 font-medium text-gray-600">
                      {s.label}
                    </span>
                  </div>
                  {index < 3 && (
                    <div
                      className={`w-16 h-1 mx-2 mb-6 ${
                        step > s.num ? 'bg-teal-600' : 'bg-gray-200'
                      }`}
                    ></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Checkout Content */}
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Forms */}
              <div className="lg:col-span-2 space-y-8">
                {/* Contact Information */}
                <div className="bg-white rounded-xl p-8 shadow-md">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Contact Information
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Email address *"
                        required
                        className="w-full px-4 py-3 border-b-2 border-gray-200 focus:border-teal-600 focus:outline-none transition-colors"
                      />
                    </div>
                    <div>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Phone number"
                        className="w-full px-4 py-3 border-b-2 border-gray-200 focus:border-teal-600 focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="bg-white rounded-xl p-8 shadow-md">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Shipping Address
                  </h2>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="First name *"
                        required
                        className="px-4 py-3 border-b-2 border-gray-200 focus:border-teal-600 focus:outline-none transition-colors"
                      />
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder="Last name *"
                        required
                        className="px-4 py-3 border-b-2 border-gray-200 focus:border-teal-600 focus:outline-none transition-colors"
                      />
                    </div>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Address *"
                      required
                      className="w-full px-4 py-3 border-b-2 border-gray-200 focus:border-teal-600 focus:outline-none transition-colors"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        placeholder="City *"
                        required
                        className="px-4 py-3 border-b-2 border-gray-200 focus:border-teal-600 focus:outline-none transition-colors"
                      />
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        placeholder="State *"
                        required
                        className="px-4 py-3 border-b-2 border-gray-200 focus:border-teal-600 focus:outline-none transition-colors"
                      />
                      <input
                        type="text"
                        name="zip"
                        value={formData.zip}
                        onChange={handleInputChange}
                        placeholder="ZIP code *"
                        required
                        className="px-4 py-3 border-b-2 border-gray-200 focus:border-teal-600 focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className="bg-white rounded-xl p-8 shadow-md">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Payment Method
                  </h2>

                  {/* Payment Method Tabs */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('card')}
                      className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                        paymentMethod === 'card'
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <i className="ri-bank-card-line text-2xl text-gray-700"></i>
                        <span className="text-sm font-semibold text-gray-700">Credit Card</span>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setPaymentMethod('paypal')}
                      className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                        paymentMethod === 'paypal'
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <i className="ri-paypal-line text-2xl text-blue-600"></i>
                        <span className="text-sm font-semibold text-gray-700">PayPal</span>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setPaymentMethod('apple')}
                      className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                        paymentMethod === 'apple'
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <i className="ri-apple-line text-2xl text-gray-900"></i>
                        <span className="text-sm font-semibold text-gray-700">Apple Pay</span>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setPaymentMethod('google')}
                      className={`p-4 rounded-lg border-2 transition-all cursor-pointer ${
                        paymentMethod === 'google'
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <i className="ri-google-line text-2xl text-red-600"></i>
                        <span className="text-sm font-semibold text-gray-700">Google Pay</span>
                      </div>
                    </button>
                  </div>

                  {/* Credit/Debit Card Form */}
                  {paymentMethod === 'card' && (
                    <div className="space-y-4">
                      <div className="flex gap-4 mb-6">
                        <div className="w-12 h-8 flex items-center justify-center">
                          <i className="ri-visa-line text-3xl text-blue-600"></i>
                        </div>
                        <div className="w-12 h-8 flex items-center justify-center">
                          <i className="ri-mastercard-line text-3xl text-red-600"></i>
                        </div>
                        <div className="w-12 h-8 flex items-center justify-center">
                          <i className="ri-bank-card-line text-3xl text-orange-600"></i>
                        </div>
                        <div className="w-12 h-8 flex items-center justify-center">
                          <i className="ri-bank-card-2-line text-3xl text-blue-800"></i>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-4">
                        We accept Visa, Mastercard, American Express, and Discover
                      </p>
                      <input
                        type="text"
                        name="cardName"
                        value={formData.cardName}
                        onChange={handleInputChange}
                        placeholder="Cardholder name *"
                        required={paymentMethod === 'card'}
                        className="w-full px-4 py-3 border-b-2 border-gray-200 focus:border-teal-600 focus:outline-none transition-colors"
                      />
                      <input
                        type="text"
                        name="cardNumber"
                        value={formData.cardNumber}
                        onChange={handleInputChange}
                        placeholder="Card number *"
                        required={paymentMethod === 'card'}
                        maxLength={16}
                        className="w-full px-4 py-3 border-b-2 border-gray-200 focus:border-teal-600 focus:outline-none transition-colors"
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="text"
                          name="expiry"
                          value={formData.expiry}
                          onChange={handleInputChange}
                          placeholder="MM / YY *"
                          required={paymentMethod === 'card'}
                          maxLength={5}
                          className="px-4 py-3 border-b-2 border-gray-200 focus:border-teal-600 focus:outline-none transition-colors"
                        />
                        <input
                          type="text"
                          name="cvv"
                          value={formData.cvv}
                          onChange={handleInputChange}
                          placeholder="CVV *"
                          required={paymentMethod === 'card'}
                          maxLength={4}
                          className="px-4 py-3 border-b-2 border-gray-200 focus:border-teal-600 focus:outline-none transition-colors"
                        />
                      </div>
                    </div>
                  )}

                  {/* PayPal Form */}
                  {paymentMethod === 'paypal' && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <div className="flex items-start gap-3">
                          <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                          <div>
                            <p className="text-sm text-blue-900 font-semibold mb-1">
                              Pay securely with PayPal
                            </p>
                            <p className="text-sm text-blue-800">
                              You'll be redirected to PayPal to complete your purchase securely.
                            </p>
                          </div>
                        </div>
                      </div>
                      <input
                        type="email"
                        name="paypalEmail"
                        value={formData.paypalEmail}
                        onChange={handleInputChange}
                        placeholder="PayPal email address *"
                        required={paymentMethod === 'paypal'}
                        className="w-full px-4 py-3 border-b-2 border-gray-200 focus:border-teal-600 focus:outline-none transition-colors"
                      />
                    </div>
                  )}

                  {/* Apple Pay Info */}
                  {paymentMethod === 'apple' && (
                    <div className="space-y-4">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                        <i className="ri-apple-line text-5xl text-gray-900 mb-4"></i>
                        <p className="text-gray-700 font-semibold mb-2">
                          Pay with Apple Pay
                        </p>
                        <p className="text-sm text-gray-600 mb-4">
                          Complete your purchase quickly and securely using Apple Pay on your Apple device.
                        </p>
                        <div className="inline-flex items-center gap-2 text-sm text-gray-500">
                          <i className="ri-shield-check-line text-green-600"></i>
                          <span>Secure and encrypted</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Google Pay Info */}
                  {paymentMethod === 'google' && (
                    <div className="space-y-4">
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                        <i className="ri-google-line text-5xl text-red-600 mb-4"></i>
                        <p className="text-gray-700 font-semibold mb-2">
                          Pay with Google Pay
                        </p>
                        <p className="text-sm text-gray-600 mb-4">
                          Use your saved payment methods from Google for a fast and secure checkout.
                        </p>
                        <div className="inline-flex items-center gap-2 text-sm text-gray-500">
                          <i className="ri-shield-check-line text-green-600"></i>
                          <span>Protected by Google</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl p-8 shadow-md sticky top-32">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">
                    Order Summary
                  </h2>

                  {/* Cart Items */}
                  <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                    {cartItems.map((item) => (
                      <div key={item.id} className="flex gap-4 pb-4 border-b border-gray-100 last:border-0">
                        <div className="w-20 h-20 flex-shrink-0">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover object-top rounded-lg"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm text-gray-900 line-clamp-2 mb-1">
                            {item.name}
                          </h3>
                          
                          {/* Quantity Controls */}
                          <div className="flex items-center gap-2 mb-2">
                            <button
                              onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                              className="w-6 h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded transition-colors cursor-pointer"
                              type="button"
                            >
                              <i className="ri-subtract-line text-sm"></i>
                            </button>
                            <span className="text-sm font-medium text-gray-700 min-w-[20px] text-center">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                              className="w-6 h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded transition-colors cursor-pointer"
                              type="button"
                            >
                              <i className="ri-add-line text-sm"></i>
                            </button>
                          </div>

                          <div className="flex items-center justify-between">
                            <p className="font-bold text-teal-600">${(item.price * item.quantity).toFixed(2)}</p>
                            
                            {/* Remove Button */}
                            <button
                              onClick={() => handleRemoveItem(item.id)}
                              className="text-red-500 hover:text-red-700 transition-colors cursor-pointer"
                              type="button"
                              title="Remove item"
                            >
                              <i className="ri-delete-bin-line text-lg"></i>
                            </button>
                          </div>

                          {item.recurring && (
                            <span className="inline-block px-2 py-0.5 bg-emerald-600/10 text-emerald-600 text-xs font-semibold rounded mt-1">
                              Monthly
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Price Breakdown */}
                  <div className="space-y-3 mb-6 pt-6 border-t border-gray-200">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Tax</span>
                      <span>${tax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Shipping</span>
                      <span className="text-emerald-600 font-semibold">Free</span>
                    </div>
                    <div className="pt-3 border-t border-gray-200">
                      <div className="flex justify-between text-xl font-bold text-gray-900">
                        <span>Total</span>
                        <span>${total.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="pt-3 border-t-2 border-accent-green/20 bg-accent-green/5 -mx-4 px-4 py-3 rounded-lg">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-semibold text-accent-green">Your Commission:</span>
                        <span className="text-2xl font-bold text-accent-green">${totalCommission.toFixed(2)}</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">You earn 30% on this order!</p>
                    </div>
                  </div>

                  {/* Checkout Button */}
                  <button
                    type="submit"
                    disabled={processing}
                    className="w-full py-4 bg-gradient-to-r from-primary to-accent-green text-white rounded-lg font-bold hover:shadow-xl transition-all mb-4 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processing ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        Processing...
                      </span>
                    ) : (
                      <>
                        {paymentMethod === 'card' && 'Complete Purchase'}
                        {paymentMethod === 'paypal' && 'Continue to PayPal'}
                        {paymentMethod === 'apple' && 'Pay with Apple Pay'}
                        {paymentMethod === 'google' && 'Pay with Google Pay'}
                      </>
                    )}
                  </button>

                  {/* Security Badges */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                      <i className="ri-shield-check-line text-accent-green"></i>
                      <span>Secure SSL Encrypted Checkout</span>
                    </div>
                    <div className="flex items-center justify-center gap-4 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-lock-line"></i>
                        256-bit SSL
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-shield-check-line"></i>
                        PCI Compliant
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CheckoutPage;